import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import apiClient from "../../../apiClient";
import Confetti from "react-confetti";
import "./LoginPage.css";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../config/msalConfig";

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [randomEmoji, setRandomEmoji] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [welcomeMessage] = useState(
    "Welcome to our People Plan App, please login using your credentials"
  );

  // Use ref to prevent multiple simultaneous processing attempts
  const isProcessingRef = useRef(false);
  const hasProcessedAccountRef = useRef(false);

  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const emojis = ["🎉", "🚀", "✨", "🎊"];

  // Check if already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      console.log("🔑 Already authenticated, redirecting to dashboard...");
      navigate("/dashboard");
    }
  }, [navigate]);

  // Process Microsoft login response (for both popup and redirect)
  const processMicrosoftAccount = useCallback(async (account, idToken = null) => {
    // Use ref for synchronous check to prevent race conditions
    if (isProcessingRef.current || localStorage.getItem("authToken")) {
      console.log("⏭️ Skipping - already processing or authenticated");
      return;
    }

    // Set ref immediately (synchronous) to block other calls
    isProcessingRef.current = true;
    hasProcessedAccountRef.current = true;
    setIsProcessing(true);

    try {
      const email = account?.username ||
        account?.idTokenClaims?.preferred_username ||
        account?.idTokenClaims?.email;

      if (!email) {
        console.error("❌ No email found in Microsoft account");
        setErrorMessage("Unable to determine email from Microsoft account.");
        return;
      }

      console.log("📧 Processing Microsoft account:", email);

      // Use provided token or acquire one silently
      let microsoftToken = idToken;
      if (!microsoftToken) {
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account
          });
          microsoftToken = tokenResponse.idToken;
        } catch (tokenError) {
          console.error("❌ Token acquisition failed:", tokenError);
          setErrorMessage("Unable to retrieve Microsoft token.");
          return;
        }
      }

      if (!microsoftToken) {
        console.error("❌ No token found");
        setErrorMessage("Unable to retrieve Microsoft token.");
        return;
      }

      console.log("🔑 Sending token to backend for validation...");

      const { data } = await apiClient.post("/api/login/microsoft", {
        email,
        microsoftToken
      });

      if (data.message === "Login successful") {
        console.log("✅ Backend validation successful");

        localStorage.setItem("authToken", data.token);
        localStorage.setItem("userRole", data.role?.toLowerCase() ?? "");
        localStorage.setItem("userEmail", data.email ?? "");
        localStorage.setItem("userId", data.userId ?? "");
        localStorage.setItem("userName", data.name ?? "");
        localStorage.setItem("lastActivity", Date.now().toString());

        setLoginSuccess(true);
        setRandomEmoji(emojis[Math.floor(Math.random() * emojis.length)]);
        setShowConfetti(true);

        setTimeout(() => {
          setShowConfetti(false);
          navigate("/dashboard");
        }, 2500);
      }
    } catch (error) {
      console.error("❌ Error processing Microsoft account:", error);
      // Reset refs on error so user can retry
      isProcessingRef.current = false;
      hasProcessedAccountRef.current = false;

      if (error.response?.status === 400 || error.response?.status === 401) {
        setErrorMessage("Your account is not authorized. Please contact HR.");
      } else if (error.response?.status === 403) {
        setErrorMessage("Access denied. You don't have an assigned role in the system.");
      } else {
        setErrorMessage("Microsoft login failed. Please try again.");
      }
    } finally {
      setIsProcessing(false);
      // Note: We don't reset isProcessingRef here on success - we want to keep blocking
      // Only reset on error (done above) to allow retry
    }
  }, [instance, navigate, emojis]);

  // Handle redirect response when returning from Microsoft
  useEffect(() => {
    // Skip if already processing or authenticated
    if (inProgress !== "none" || localStorage.getItem("authToken") || isProcessingRef.current) {
      return;
    }

    instance.handleRedirectPromise()
      .then((response) => {
        if (response && response.account && !isProcessingRef.current) {
          console.log("✅ Redirect login completed, processing account...");
          processMicrosoftAccount(response.account, response.idToken);
        }
      })
      .catch((error) => {
        console.error("Redirect error:", error);
        if (error.errorCode !== "no_cached_authority") {
          setErrorMessage("Microsoft login failed. Please try again.");
        }
      });
  }, [instance, inProgress, processMicrosoftAccount]);

  // Check if already logged in via Microsoft (e.g., after redirect)
  useEffect(() => {
    // Only process if: has accounts, no auth token, not currently processing, and hasn't already been processed
    if (
      accounts.length > 0 &&
      !localStorage.getItem("authToken") &&
      inProgress === "none" &&
      !isProcessingRef.current &&
      !hasProcessedAccountRef.current
    ) {
      console.log("📋 Found existing Microsoft account, processing...");
      processMicrosoftAccount(accounts[0]);
    }
  }, [accounts, inProgress, processMicrosoftAccount]);

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log("Login attempt with email:", username);

    try {
      const { data } = await apiClient.post("/api/login", {
        username,
        password,
      });

      if (data.message === "Login successful") {
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("userRole", data.role?.toLowerCase() ?? "");
        localStorage.setItem("userEmail", data.email ?? "");
        localStorage.setItem("userId", data.userId ?? "");
        localStorage.setItem("userName", data.name ?? "");
        localStorage.setItem("lastActivity", Date.now().toString());

        setLoginSuccess(true);
        setRandomEmoji(emojis[Math.floor(Math.random() * emojis.length)]);
        setShowConfetti(true);

        setTimeout(() => {
          setShowConfetti(false);
          navigate("/dashboard");
        }, 2500);
      }
    } catch (error) {
      if (error.response?.status === 423) {
        setErrorMessage(
          "Account temporarily locked due to multiple failed attempts. Please try again later."
        );
      } else if (error.response?.status === 429) {
        setErrorMessage("Too many login attempts. Please try again later.");
      } else {
        setErrorMessage("Invalid credentials, please try again.");
      }
      console.error("Login error:", error);
    }
  };

  const handleMicrosoftLogin = async () => {
    setErrorMessage("");

    // Prevent double-clicks
    if (isProcessing || inProgress !== "none") {
      console.log("⏭️ Login already in progress, skipping...");
      return;
    }

    try {
      console.log("🔵 Initiating Microsoft login...");

      // Try popup first, fall back to redirect if blocked
      let loginResponse;
      try {
        loginResponse = await instance.loginPopup({
          ...loginRequest,
          prompt: "select_account"
        });
        console.log("✅ Microsoft popup completed");

        // Process the popup response immediately with the idToken
        if (loginResponse?.account) {
          await processMicrosoftAccount(loginResponse.account, loginResponse.idToken);
        }
      } catch (popupError) {
        // If popup is blocked or COOP issues, use redirect
        if (popupError.errorCode === "popup_window_error" ||
            popupError.errorCode === "empty_window_error" ||
            popupError.message?.includes("Cross-Origin-Opener-Policy")) {
          console.log("⚠️ Popup blocked, using redirect...");
          await instance.loginRedirect(loginRequest);
          return; // Redirect will reload the page
        }
        throw popupError;
      }
    } catch (error) {
      console.error("❌ Microsoft login error:", error);

      if (error.errorCode === "user_cancelled") {
        setErrorMessage("Microsoft login was cancelled.");
      } else if (error.response?.status === 400 || error.response?.status === 401) {
        setErrorMessage("Your account is not authorized. Please contact HR.");
      } else if (error.response?.status === 403) {
        setErrorMessage("Access denied. You don't have an assigned role in the system.");
      } else {
        setErrorMessage("Microsoft login failed. Please try again.");
      }
    }
  };

  // Show loading state while processing Microsoft login
  if (isProcessing || (inProgress !== "none" && inProgress !== "startup")) {
    return (
      <div className="login-page">
        <div className="success-container">
          <div className="success-card">
            <div className="success-emoji">🔐</div>
            <h1 className="success-title">Authenticating...</h1>
            <p className="success-subtitle">Please wait while we verify your account</p>
            <div className="success-loader">
              <div className="loader-bar"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {showConfetti && <Confetti />}

      {loginSuccess ? (
        <div className="success-container">
          <div className="success-card">
            <div className="success-emoji">{randomEmoji}</div>
            <h1 className="success-title">Welcome Back!</h1>
            <p className="success-subtitle">Login Successful</p>
            <div className="success-loader">
              <div className="loader-bar"></div>
            </div>
            <p className="success-redirect">Redirecting to dashboard...</p>
          </div>
        </div>
      ) : (
        <div className="login-container">
          <h2 className="welcome-message">{welcomeMessage}</h2>

          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="username">Email Address:</label>
              <input
                type="email"
                id="username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your.name@cafuamanagement.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password:</label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {errorMessage && (
              <div className="error-message-box">
                <p className="error-message">{errorMessage}</p>
              </div>
            )}

            <button type="submit" className="login-button">
              Login
            </button>
          </form>

          <div className="login-footer">
            <Link to="/forgot-password" className="link-style-button">
              Forgot Password?
            </Link>

            <p className="support-message">
              If you need further support, please submit a ticket in the&nbsp;
              <a
                href="https://cafuamanagementcompany.sharepoint.com/sites/CafuaManagement/SitePages/Human-Resources.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="support-link"
              >
                <strong>HR Requests Ticketing System</strong>
              </a>
            </p>
          </div>

          <div style={{ padding: "20px 50px" }}>
            <button
              onClick={handleMicrosoftLogin}
              className="microsoft-login-button"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                alt="Microsoft logo"
                style={{ width: "20px", height: "20px", marginRight: "10px" }}
              />
              Login with Microsoft
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;